import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNavigationBar from './TopNavigationBar';
import {
	Button,
	Grid2,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { ChevronDownIcon, CloseIcon } from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { getMsgReadStatus } from '../context/MainProvider';
import { onAddClickStat } from '../services/UserService';
import { getUserEmail, getUserId } from '../Utils/AuthUtil';
import {
	confirmDiscardFacebookChatDraft,
	notifyFacebookChatNavigation,
	setFacebookChatDraftDirty,
} from '../Utils/NavigationGuard';

interface CustomDrawerProps {
	tabName: string;
	listItems: Array<NavigationItem>;
	onClickTab: (value: string) => void;
	onClose?: () => void;
	onClickLogout?: () => void;
	isScreenInMobileMode?: boolean;
}

interface NavigationItem {
	name: string;
	route: string;
	unread?: boolean;
	children?: Array<{
		name: string;
		route: string;
	}>;
}

function CustomDrawer({
	tabName,
	listItems,
	onClickTab,
	onClose,
	onClickLogout,
	isScreenInMobileMode = false,
}: CustomDrawerProps) {
	const drawerWidth = 240;
	const navigate = useNavigate();
	const [expandedMenus, setExpandedMenus] = useState<Array<string>>([]);

	function getIconColor(selectedTabName: string, route: string) {
		if (
			selectedTabName === route ||
			selectedTabName.substring(0, 8) === route
		) {
			return MAIN_THEME_COLOR;
		} else {
			return '#7C7C7C';
		}
	}

	function isSelectedRoute(route: string) {
		return tabName === route || tabName.substring(0, 8) === route;
	}

	function hasSelectedChild(item: NavigationItem) {
		return item.children?.some(child => tabName === child.route) || false;
	}

	function isExpanded(item: NavigationItem) {
		return expandedMenus.includes(item.route);
	}

	function onClickParentItem(item: NavigationItem) {
		if (isExpanded(item)) {
			setExpandedMenus(value =>
				value.filter(route => route !== item.route),
			);
			return;
		}

		setExpandedMenus(value => [...value, item.route]);
		requestNavigation(item.children?.[0]?.route || item.route);
	}

	function onToggleParentItem(item: NavigationItem) {
		setExpandedMenus(value =>
			value.includes(item.route)
				? value.filter(route => route !== item.route)
				: [...value, item.route],
		);
	}

	function requestNavigation(route: string) {
		if (!confirmDiscardFacebookChatDraft()) {
			return;
		}

		navigateToRoute(route);
	}

	function navigateToRoute(route: string) {
		setFacebookChatDraftDirty(false);
		notifyFacebookChatNavigation(route);
		onClickTab(route);
		navigate(route);
		onClose?.();
	}

	useEffect(() => {
		const selectedParentRoutes = listItems
			.filter(item => hasSelectedChild(item))
			.map(item => item.route);

		setExpandedMenus(value =>
			Array.from(new Set([...value, ...selectedParentRoutes])),
		);
	}, [tabName, listItems]);

	return (
		<Drawer
			sx={{
				width: isScreenInMobileMode ? '100%' : drawerWidth,
				boxSizing: 'border-box',
				borderWidth: '0px',
				marginTop: isScreenInMobileMode ? '0px' : '50px',
				height: isScreenInMobileMode ? '100%' : '95%',
			}}
			variant="permanent"
			anchor="left"
			elevation={3}>
			{isScreenInMobileMode && (
				<Toolbar
					style={{
						paddingLeft: '20px',
						minHeight: '52px',
						justifyContent: 'space-between',
					}}>
					<Typography
						fontSize={'18px'}
						fontWeight={'700'}
						color={MAIN_THEME_COLOR}
						paddingLeft="5px">
						AskAI
					</Typography>
					<Grid2
						sx={{
							flexDirection: 'column',
						}}>
						<Button
							sx={{
								marginRight: '5px',
								color: MAIN_THEME_COLOR,
								textTransform: 'none',
							}}
							onClick={onClickLogout}>
							Logout
						</Button>
						<IconButton onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Grid2>
				</Toolbar>
			)}
			<List style={{ marginTop: '15px' }}>
				{listItems.map((item, index) => {
					const hasChildren = Boolean(item.children?.length);
					const isParentSelected =
						isSelectedRoute(item.route) || hasSelectedChild(item);

					return (
						<Box key={index}>
							<ListItem
								disablePadding
								style={{
									paddingLeft: '20px',
									paddingRight: '20px',
									minHeight: '52px',
									justifyContent: 'space-between',
								}}>
								<ListItemButton
									sx={{
										justifyContent: 'space-between',
										backgroundColor: isParentSelected
											? '#EBFAF8'
											: 'transparent',
										borderRadius: '6px',
										minHeight: '36px',
										paddingX: '10px',
										'&:hover': {
											backgroundColor: isParentSelected
												? '#EBFAF8'
												: '#F7FBFB',
										},
									}}
									onClick={() =>
										hasChildren
											? onClickParentItem(item)
											: requestNavigation(item.route)
									}>
									<Typography
										style={{
											width: '90%',
										}}>
										{item.name}
									</Typography>
									{hasChildren ? (
										<IconButton
											size="small"
											sx={{ padding: '2px' }}
											onClick={event => {
												event.stopPropagation();
												onToggleParentItem(item);
											}}>
											<ChevronDownIcon
												color={getIconColor(
													tabName,
													item.route,
												)}
												style={{
													transform: isExpanded(item)
														? 'rotate(180deg)'
														: 'rotate(0deg)',
													transition:
														'transform 0.2s ease',
												}}
											/>
										</IconButton>
									) : (
										item.unread && (
											<Box
												sx={{
													width: 8,
													height: 8,
													borderRadius: '50%',
													backgroundColor:
														MAIN_THEME_COLOR,
												}}
											/>
										)
									)}
								</ListItemButton>
							</ListItem>
							{hasChildren && (
								<Collapse
									in={isExpanded(item)}
									timeout="auto"
									unmountOnExit>
									<List component="div" disablePadding>
										{item.children?.map(child => (
											<ListItem
												key={child.route}
												disablePadding
												style={{
													paddingLeft: '20px',
													paddingRight: '20px',
													minHeight: '42px',
												}}>
												<ListItemButton
													sx={{
														justifyContent:
															'space-between',
														backgroundColor:
															isSelectedRoute(
																child.route,
															)
																? '#EBFAF8'
																: 'transparent',
														borderRadius: '6px',
														minHeight: '34px',
														paddingX: '10px',
														'&:hover': {
															backgroundColor:
																isSelectedRoute(
																	child.route,
																)
																	? '#EBFAF8'
																	: '#F7FBFB',
														},
													}}
													onClick={() =>
														requestNavigation(
															child.route,
														)
													}>
													<Typography
														fontSize="14px"
														style={{
															width: '90%',
														}}>
														{child.name}
													</Typography>
												</ListItemButton>
											</ListItem>
										))}
									</List>
								</Collapse>
							)}
						</Box>
					);
				})}
			</List>
		</Drawer>
	);
}

export default function SideNavigationBar() {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isUnreadMsgInChat, isUnreadMsgInStudio, updateMsgStatus } =
		getMsgReadStatus();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [tabName, setTabName] = useState('/');
	const [openDrawerInMobile, setOpenDrawerInMobile] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const listItems = [
		{
			name: 'Home',
			route: '/home',
			unread: false,
		},
		// {
		// 	name: 'Activity Log',
		// 	route: '/chatHistory',
		// },
		// {
		// 	name: 'Consultant Assistance',
		// 	route: '/agentRequest',
		// },
		{
			name: 'Studio',
			route: '/knowledgeUpload',
			unread: isUnreadMsgInStudio,
		},
		{
			name: 'Conversations',
			route: '/chats',
			unread: isUnreadMsgInChat,
		},
		{
			name: 'Social',
			route: '/social',
			children: [
				{
					name: 'Facebook',
					route: '/social/facebook',
				},
			],
		},
	];

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	function openDrawerInMobileMode() {
		setOpenDrawerInMobile(!openDrawerInMobile);
	}

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	function onClickLogout() {
		localStorage.clear();
		navigate('/login', { replace: true });
	}

	function onAddClickStats(route: string) {
		const item =
			listItems.find(e => e.route === route)?.name ||
			listItems
				.flatMap(e => e.children || [])
				.find(e => e.route === route)?.name;
		onAddClickStat(
			getUserId() ?? '',
			`${item} tab`,
			true,
			'Navigation Bar',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	if (isMobile) {
		return (
			<>
				<CssBaseline />
				<TopNavigationBar
					onClickLogo={openDrawerInMobileMode}
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				{openDrawerInMobile && (
					<CustomDrawer
						tabName={tabName}
						listItems={listItems}
						onClickTab={value => {
							setTabName(value);
							updateMsgStatus(value);
							onAddClickStats(value);
						}}
						isScreenInMobileMode={isMobile}
						onClose={openDrawerInMobileMode}
						onClickLogout={onClickLogout}
					/>
				)}
			</>
		);
	} else {
		return (
			<Box sx={{ display: 'flex' }}>
				<CssBaseline />
				<TopNavigationBar
					onClickLogout={onClickLogout}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				<CustomDrawer
					tabName={tabName}
					listItems={listItems}
					onClickTab={value => {
						setTabName(value);
						updateMsgStatus(value);
						onAddClickStats(value);
					}}
				/>
			</Box>
		);
	}
}
